import * as React from 'react'
import dynamic from 'next/dynamic'
import classNames from 'classnames'
import {motion} from 'framer-motion'

import Header from '@components/demo/Header'
import Footer from '@components/demo/Footer'
import Contact from '@components/demo/Contact'
import ImgSet from '@components/demo/ImgSet'
import UploadResult from '@components/demo/UploadResult'
import CTAButton from '@components/demo/CTAButton'

import {t, axios} from '@lib/helpers'

const Meta = dynamic(() => import('@components/Meta'))

const Page = ({isRecaptchaValidated}) => {
  return (
    <React.Fragment>
      <Meta title={`WowFaceVerificationAPI – ${t('layout.title')}`} />

      <div className='DemoDetail FaceVerificationAPI'>
        <Header name='WowFaceVerificationAPI' />

        <div className='DemoDetail__wrapper'>
          <Hero />
          <Why />
        </div>

        <Upload isRecaptchaValidated={isRecaptchaValidated} />

        <Contact isRecaptchaValidated={isRecaptchaValidated}>
          <div className='DemoDetail__spec'>
            <div className='container'>
              <p className='DemoDetail__spec__p'>
                {t('pages.demo.face_verification.spec')}
              </p>
              <img
                src='https://datawow-demo.s3-ap-southeast-1.amazonaws.com/accuracy.png'
                loading='lazy'
              />
            </div>
          </div>
        </Contact>

        <Footer />
      </div>
    </React.Fragment>
  )
}

const Hero = () => {
  const variants = {
    container: {
      hidden: {opacity: 0},
      show: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1,
        },
      },
    },
    item: {
      hidden: {
        opacity: 0,
        y: 100,
      },
      show: {
        opacity: 1,
        y: 0,
      },
    },
  }

  const delay = 1

  return (
    <section className='DemoDetail__hero'>
      <div className='container'>
        <div className='DemoDetail__hero__ratio'>
          <motion.div
            className='DemoDetail__hero__inner'
            variants={variants.container}
            initial='hidden'
            animate='show'
          >
            <motion.div className='tower1' variants={variants.item}>
              <ImgSet src='/images/demo/face-verfication-api/hero/tower.png' />
            </motion.div>
            <motion.div className='tower2' variants={variants.item}>
              <ImgSet src='/images/demo/face-verfication-api/hero/tower.png' />
            </motion.div>

            <motion.div className='face-panel' variants={variants.item}>
              <motion.div
                animate={{x: 20, scale: 1.05}}
                transition={{
                  delay,
                  yoyo: Infinity,
                  duration: 10,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/hero/face-panel.png' />
              </motion.div>
            </motion.div>

            <motion.div className='head' variants={variants.item}>
              <motion.div
                animate={{y: -20}}
                transition={{
                  duration: 4,
                  yoyo: Infinity,
                  delay,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/hero/head.png' />
                <motion.div
                  animate={{y: '230%'}}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    delay,
                  }}
                  className='curve'
                >
                  <ImgSet src='/images/demo/face-verfication-api/hero/curve.png' />
                </motion.div>
              </motion.div>
            </motion.div>

            <motion.div className='stand-right' variants={variants.item}>
              <ImgSet src='/images/demo/face-verfication-api/hero/stand-right.png' />
            </motion.div>

            <motion.div className='human2' variants={variants.item}>
              <ImgSet src='/images/demo/face-verfication-api/hero/human2.png' />
            </motion.div>

            <motion.div className='stand-with-mobile' variants={variants.item}>
              <motion.div
                animate={{y: 40}}
                transition={{
                  delay,
                  repeatDelay: 4,
                  yoyo: Infinity,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/hero/stand-with-mobile.png' />
              </motion.div>
            </motion.div>

            <motion.div className='stand-left' variants={variants.item}>
              <motion.div
                animate={{y: -40}}
                transition={{
                  delay: delay * 2,
                  repeatDelay: 3,
                  yoyo: Infinity,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/hero/stand-left.png' />
              </motion.div>
            </motion.div>

            <motion.div className='human1' variants={variants.item}>
              <motion.div
                animate={{y: -40}}
                transition={{
                  delay: delay * 2,
                  repeatDelay: 3,
                  yoyo: Infinity,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/hero/human1.png' />
              </motion.div>
            </motion.div>

            <motion.div className='stand-with-human' variants={variants.item}>
              <ImgSet src='/images/demo/face-verfication-api/hero/stand-with-human.png' />
            </motion.div>

            <motion.div className='tube1' variants={variants.item}>
              <motion.div
                animate={{y: -20}}
                transition={{
                  yoyo: Infinity,
                  duration: 4,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/hero/tube1.png' />
              </motion.div>
            </motion.div>

            <motion.div className='tube2' variants={variants.item}>
              <motion.div
                animate={{y: 20, x: 10}}
                transition={{
                  yoyo: Infinity,
                  duration: 4,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/hero/tube2.png' />
              </motion.div>
            </motion.div>

            <motion.div className='tube3' variants={variants.item}>
              <motion.div
                animate={{y: -20, x: 10}}
                transition={{
                  yoyo: Infinity,
                  duration: 4,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/hero/tube2.png' />
              </motion.div>
            </motion.div>
            <motion.div className='tube4' variants={variants.item}>
              <motion.div
                animate={{y: -10, x: 20}}
                transition={{
                  yoyo: Infinity,
                  duration: 4,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/hero/tube1.png' />
              </motion.div>
            </motion.div>

            <motion.div className='guard' variants={variants.item}>
              <motion.div
                animate={{x: 50, scale: 0.9}}
                transition={{
                  yoyo: Infinity,
                  duration: 4,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/hero/guard.png' />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        <div className='DemoDetail__hero__content'>
          <h1 className='DemoDetail__title DemoDetail__title--hilight'>
            {t('pages.demo.face_verification.hero.title')}
          </h1>
          <h4>{t('pages.demo.face_verification.hero.subtitle')}</h4>

          <CTAButton />
        </div>
      </div>
    </section>
  )
}

const Why = () => {
  return (
    <section className='DemoDetail__why'>
      <div className='container' id='why'>
        <div className='DemoDetail__scan'>
          <div className='DemoDetail__scan__ratio'>
            <ImgSet
              src='/images/demo/face-verfication-api/why/human.png'
              className='human'
            />
            <ImgSet
              src='/images/demo/face-verfication-api/why/unlock.png'
              className='unlock'
            />
            <ImgSet
              src='/images/demo/face-verfication-api/why/mobile.png'
              className='mobile'
            />

            <div className='capture-wrap'>
              <ImgSet
                src='/images/demo/face-verfication-api/why/capture.png'
                className='capture'
              />

              <motion.div
                className='gradient'
                initial={{scaleY: 0, transformOrigin: 'bottom'}}
                animate={{scaleY: 1}}
                transition={{
                  duration: 2,
                  yoyo: Infinity,
                }}
              />

              <motion.div
                className='handle'
                initial={{bottom: '0%'}}
                animate={{bottom: '90%'}}
                transition={{
                  duration: 2,
                  yoyo: Infinity,
                }}
              >
                <ImgSet src='/images/demo/face-verfication-api/why/handle.png' />
              </motion.div>
            </div>

            <div className='line' />
          </div>
        </div>

        <div className='DemoDetail__why__content'>
          <h2
            className='DemoDetail__title DemoDetail__title--hilight'
            dangerouslySetInnerHTML={{
              __html: t('pages.demo.face_verification.why.title'),
            }}
          />
          <p>{t('pages.demo.face_verification.why.description')}</p>
        </div>
      </div>
    </section>
  )
}

const Upload = ({isRecaptchaValidated}) => {
  const [files, setFiles] = React.useState({image1: null, image2: null})
  const [result, setResult] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const firstInputRef = React.useRef()
  const secondInputRef = React.useRef()

  const handleInputChange = (event) => {
    const {id, files} = event.currentTarget
    setFiles((state) => {
      return {
        ...state,
        [id]: files[0],
      }
    })
  }

  const upload = async () => {
    try {
      setResult('')
      setSubmitting(true)

      const formData = new FormData()
      formData.append('image', files.image1)
      formData.append('image_2', files.image2)

      const response = await axios.post('/demo/face_verifications', formData, {
        headers: {
          'X-Api-Key': 'Data wow',
        },
      })
      setResult(JSON.stringify(response.data.data.results, null, 2))
    } catch (err) {
      setResult('Something wrong please try again.')
    }
    setSubmitting(false)
  }

  React.useEffect(() => {
    if (files.image1 && files.image2) {
      upload()
    }
  }, [files])

  const label = submitting
    ? t('layout.loading')
    : t('pages.demo.shared.upload_form.submit')

  return (
    <section className='DemoDetail__upload' id='upload'>
      <header>
        <div className='container'>
          <h2 className='DemoDetail__title DemoDetail__title--hilight'>
            WowFaceVerificationAPI
          </h2>
        </div>
      </header>

      <hr />

      <div className='DemoDetail__upload__content'>
        <form className='DemoDetail__upload__form'>
          <h6>
            {t('pages.demo.shared.upload_form.title')} WowFaceVerificationAPI
          </h6>

          <div className='DemoDetail__upload__frame'>
            <div className='DemoDetail__upload__imgs DemoDetail__upload__imgs--two-columns'>
              {files.image1 && (
                <img
                  src={URL.createObjectURL(files.image1)}
                  alt='upload image'
                />
              )}
              {files.image2 && (
                <img
                  src={URL.createObjectURL(files.image2)}
                  alt='upload image'
                />
              )}
            </div>

            {isRecaptchaValidated ? (
              <div className='DemoDetail__upload__frame__container'>
                <img
                  src='/images/demo/picture.png'
                  srcSet='/images/demo/picture@2x.png 2x, /images/demo/picture@3x.png 3x'
                  loading='lazy'
                />

                <span>{t('pages.demo.shared.upload_form.support')}</span>

                <div className='DemoDetail__upload__button-group'>
                  <label
                    className={classNames('DemoDetail__button', {
                      'DemoDetail__button--disabled': submitting,
                    })}
                    htmlFor='image1'
                  >
                    {label}
                  </label>
                  <input
                    type='file'
                    id='image1'
                    onChange={handleInputChange}
                    accept='.jpg, .jpeg, .png'
                    ref={firstInputRef}
                  />

                  <label
                    className={classNames('DemoDetail__button', {
                      'DemoDetail__button--disabled': submitting,
                    })}
                    htmlFor='image2'
                  >
                    {label}
                  </label>

                  <input
                    type='file'
                    id='image2'
                    onChange={handleInputChange}
                    accept='.jpg, .jpeg, .png'
                    ref={secondInputRef}
                  />
                </div>
              </div>
            ) : (
              <div>{t('layout.preparing')}</div>
            )}
          </div>
        </form>

        <UploadResult result={result} />
      </div>
    </section>
  )
}

export default Page
